import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Testimonial.css";

const reviews = [
  {
    quote: "Sendcheap completely changed how I handle payments. The offline mode is a lifesaver when I'm in areas with bad network.",
    name: "Chioma Okafor",
    role: "Small Business Owner, Lagos",
    color: "#7c3aed",
  },
  {
    quote: "I use it every day for airtime, bills, and sending money. It's the fastest app I've ever used — no delays, ever.",
    name: "Emeka Nwosu",
    role: "Freelancer, Abuja",
    color: "#2563eb",
  },
  {
    quote: "The crypto feature is seamless. I sell my USDT and get naira directly to my account within seconds. Highly recommend.",
    name: "Tunde Adeyemi",
    role: "Crypto Trader, Port Harcourt",
    color: "#059669",
  },
  {
    quote: "Best fintech app in Nigeria, period. The team is responsive and the app keeps getting better with every update.",
    name: "Ngozi Eze",
    role: "CEO, TechStartup NG, Abuja",
    color: "#d97706",
  },
  {
    quote: "I needed a virtual dollar card urgently for a subscription and Sendcheap issued one in under 10 seconds. Incredible.",
    name: "Babatunde Adeleke",
    role: "Software Engineer, Lagos",
    color: "#dc2626",
  },
  {
    quote: "My boutique accepts crypto payments now thanks to Sendcheap. My customers love the option and I get naira instantly.",
    name: "Amaka Obiora",
    role: "Fashion Designer, Enugu",
    color: "#0891b2",
  },
  {
    quote: "As a student I can recharge data, pay school fees and even do transfers without always needing internet. A game changer.",
    name: "Chukwuemeka Dike",
    role: "Student, University of Lagos",
    color: "#be185d",
  },
  {
    quote: "I run a small shop in Kano and Sendcheap lets me accept transfers even when our network is down. Business never stops.",
    name: "Fatima Sule",
    role: "Entrepreneur, Kano",
    color: "#4f46e5",
  },
  {
    quote: "The UI is clean, fast and works perfectly. I switched from three different apps to just Sendcheap. No regrets.",
    name: "Oluwaseun Bello",
    role: "Product Manager, Lagos",
    color: "#b45309",
  },
  {
    quote: "Paying electricity and cable bills used to take forever. Now it's done in seconds — even when there's no network.",
    name: "Adaeze Okonkwo",
    role: "Nurse, Owerri",
    color: "#047857",
  },
  {
    quote: "I use the virtual dollar card to pay for my foreign suppliers. Works on every platform and the exchange rate is fair.",
    name: "Ibrahim Musa",
    role: "Importer, Kaduna",
    color: "#be123c",
  },
  {
    quote: "My students pay their lesson fees through Sendcheap. It's simple enough that even parents who aren't tech-savvy use it.",
    name: "Blessing Okwu",
    role: "Private Tutor, Asaba",
    color: "#6d28d9",
  },
];

const GAP = 20;

const getInitials = (name) =>
  name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase();

const getVisible = () => {
  if (typeof window === "undefined") return 3;
  if (window.innerWidth <= 768) return 1;
  if (window.innerWidth <= 1100) return 2;
  return 3;
};

const Testimonial = () => {
  const [current, setCurrent] = useState(0);
  const [cardWidth, setCardWidth] = useState(null);
  const [paused, setPaused] = useState(false);
  const outerRef = useRef(null);

  const maxIndex = reviews.length - getVisible();

  const computeLayout = useCallback(() => {
    if (!outerRef.current) return;
    const visible = getVisible();
    const outerW = outerRef.current.offsetWidth;
    setCardWidth((outerW - GAP * (visible - 1)) / visible);
  }, []);

  useEffect(() => {
    computeLayout();
    window.addEventListener("resize", computeLayout);
    return () => window.removeEventListener("resize", computeLayout);
  }, [computeLayout]);

  // Keep current in bounds on resize
  useEffect(() => {
    setCurrent((c) => Math.min(c, reviews.length - getVisible()));
  }, [cardWidth]);

  // Auto-advance
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setCurrent((c) => {
        const max = reviews.length - getVisible();
        return c >= max ? 0 : c + 1;
      });
    }, 4000);
    return () => clearInterval(timer);
  }, [paused]);

  const prev = () =>
    setCurrent((c) => (c <= 0 ? reviews.length - getVisible() : c - 1));

  const next = () =>
    setCurrent((c) => (c >= reviews.length - getVisible() ? 0 : c + 1));

  const offset = cardWidth != null ? current * (cardWidth + GAP) : 0;
  const dotCount = reviews.length - getVisible() + 1;

  return (
    <section className="testimonial">
      <div className="testi-header">
        <span className="testi-label">Testimonials</span>
        <h2 className="testi-title">
          Loved by <span className="testi-accent">10,000+ users.</span>
        </h2>
        <p className="testi-sub">
          Don't just take our word for it — hear from people who use Sendcheap every day.
        </p>
      </div>

      <div
        className="testi-carousel-wrap"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <button className="testi-nav testi-nav-prev" onClick={prev} aria-label="Previous">
          &#8592;
        </button>

        <div className="testi-viewport" ref={outerRef}>
          <div
            className="testi-track"
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {reviews.map((r, i) => (
              <div
                className="testi-card"
                key={i}
                style={cardWidth != null ? { width: cardWidth, flexShrink: 0 } : {}}
              >
                <div className="testi-stars">★★★★★</div>
                <p className="testi-quote">&ldquo;{r.quote}&rdquo;</p>
                <div className="testi-author">
                  <div
                    className="testi-avatar"
                    style={{ background: r.color }}
                  >
                    {getInitials(r.name)}
                  </div>
                  <div>
                    <div className="testi-name">{r.name}</div>
                    <div className="testi-role">{r.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button className="testi-nav testi-nav-next" onClick={next} aria-label="Next">
          &#8594;
        </button>
      </div>

      <div className="testi-dots">
        {Array.from({ length: dotCount }).map((_, i) => (
          <button
            key={i}
            className={`testi-dot${i === current ? " active" : ""}`}
            onClick={() => setCurrent(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Testimonial;
